import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserRole } from '../hooks/useUserRole';
import { processingApi, organizationApi, openPdfExport } from '../services/api';
import {
  ProcessingActivity,
  ProcessingStatus,
  Organization,
  LegalBasis,
  RiskLevel
} from '@dsgvo-buddy/types';
import {
  Title,
  Button,
  Table,
  TableColumn,
  TableRow,
  TableCell,
  Input,
  Select,
  Option,
  FlexBox,
  Text,
  Icon,
  ObjectStatus,
  BusyIndicator,
  Card,
  CardHeader
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';
import PageIntro from '../components/PageIntro';
import { pageIntros } from '../helpers/helpTexts';

const statusConfig: Record<ProcessingStatus, { label: string; state: string }> = {
  [ProcessingStatus.DRAFT]: { label: 'Entwurf', state: 'None' },
  [ProcessingStatus.IN_REVIEW]: { label: 'In Prüfung', state: 'Information' },
  [ProcessingStatus.APPROVED]: { label: 'Freigegeben', state: 'Success' },
  [ProcessingStatus.ARCHIVED]: { label: 'Archiviert', state: 'None' }
};

const riskConfig: Record<RiskLevel, { label: string; state: string }> = {
  [RiskLevel.LOW]: { label: 'Niedrig', state: 'Success' },
  [RiskLevel.MEDIUM]: { label: 'Mittel', state: 'Warning' },
  [RiskLevel.HIGH]: { label: 'Hoch', state: 'Error' }
};

const legalBasisLabels: Record<LegalBasis, string> = {
  [LegalBasis.CONSENT]: 'Einwilligung',
  [LegalBasis.CONTRACT]: 'Vertragserfüllung',
  [LegalBasis.LEGAL_OBLIGATION]: 'Rechtl. Verpflichtung',
  [LegalBasis.VITAL_INTERESTS]: 'Lebenswichtige Int.',
  [LegalBasis.PUBLIC_INTEREST]: 'Öffentl. Interesse',
  [LegalBasis.LEGITIMATE_INTEREST]: 'Berechtigtes Int.'
};

export default function ProcessingListPage() {
  const navigate = useNavigate();
  const { canCreate } = useUserRole();
  const [activities, setActivities] = useState<ProcessingActivity[]>([]);
  const [filteredActivities, setFilteredActivities] = useState<ProcessingActivity[]>([]);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [departments, setDepartments] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProcessingStatus | ''>('');
  const [departmentFilter, setDepartmentFilter] = useState('');

  useEffect(() => { loadOrganizations(); }, []);
  useEffect(() => { if (currentOrg) { loadActivities(); loadDepartments(); } }, [currentOrg]);
  useEffect(() => { applyFilters(); }, [activities, searchTerm, statusFilter, departmentFilter]);

  const loadOrganizations = async () => {
    const response = await organizationApi.getAll();
    if (response.success && response.data) {
      if (response.data.length > 0) setCurrentOrg(response.data[0]);
    }
    setLoading(false);
  };

  const loadActivities = async () => {
    if (!currentOrg) return;
    setLoading(true);
    const response = await processingApi.getByOrganization(currentOrg.id);
    if (response.success && response.data) setActivities(response.data);
    setLoading(false);
  };

  const loadDepartments = async () => {
    if (!currentOrg) return;
    const response = await processingApi.getDepartments(currentOrg.id);
    if (response.success && response.data) setDepartments(response.data);
  };

  const applyFilters = () => {
    let filtered = [...activities];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(a =>
        a.name.toLowerCase().includes(term) ||
        a.description?.toLowerCase().includes(term) ||
        a.purpose.toLowerCase().includes(term) ||
        a.department.toLowerCase().includes(term)
      );
    }
    if (statusFilter) filtered = filtered.filter(a => a.status === statusFilter);
    if (departmentFilter) filtered = filtered.filter(a => a.department === departmentFilter);
    setFilteredActivities(filtered);
  };

  if (loading && !currentOrg) {
    return (
      <FlexBox alignItems="Center" justifyContent="Center" style={{ height: '100%' }}>
        <BusyIndicator active size="Large" />
      </FlexBox>
    );
  }

  return (
    <div className="page-layout">
      {/* Page Header */}
      <FlexBox justifyContent="SpaceBetween" alignItems="Center" style={{ marginBottom: '1.5rem' }}>
        <div>
          <Title level="H3" style={{ marginBottom: '0.25rem' }}>Verarbeitungstätigkeiten</Title>
          <Text style={{ color: 'var(--sapNeutralTextColor)' }}>
            Verzeichnis gemäß Art. 30 DSGVO
          </Text>
        </div>
        <FlexBox style={{ gap: '0.5rem' }}>
          <Button icon="pdf-attachment" design="Default" onClick={() => currentOrg && openPdfExport('vvt', currentOrg.id)}>PDF Export</Button>
          {canCreate && (
            <Button icon="add" design="Emphasized" onClick={() => navigate('/processing/new')}>
              Neue Verarbeitung
            </Button>
          )}
        </FlexBox>
      </FlexBox>

      <PageIntro {...pageIntros.processing} storageKey="processing" />

      {/* Filter Bar */}
      <FlexBox
        alignItems="End"
        wrap="Wrap"
        style={{
          gap: '0.75rem',
          marginBottom: '1rem',
          padding: '1rem',
          background: 'var(--sapObjectHeader_Background, var(--sapBaseColor))',
          borderRadius: '0.5rem',
          border: '1px solid var(--sapGroup_TitleBorderColor, #d9d9d9)'
        }}
      >
        <div style={{ flex: '1 1 300px', minWidth: '200px' }}>
          <Text style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.25rem' }}>Suche</Text>
          <Input
            placeholder="Name, Beschreibung, Abteilung..."
            icon={<Icon name="search" />}
            value={searchTerm}
            onInput={(e: any) => setSearchTerm(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>

        <div style={{ flex: '0 0 180px' }}>
          <Text style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.25rem' }}>Status</Text>
          <Select onChange={(e: any) => setStatusFilter(e.detail.selectedOption.value)} style={{ width: '100%' }}>
            <Option value="">Alle Status</Option>
            <Option value={ProcessingStatus.DRAFT}>Entwurf</Option>
            <Option value={ProcessingStatus.IN_REVIEW}>In Prüfung</Option>
            <Option value={ProcessingStatus.APPROVED}>Freigegeben</Option>
            <Option value={ProcessingStatus.ARCHIVED}>Archiviert</Option>
          </Select>
        </div>

        <div style={{ flex: '0 0 180px' }}>
          <Text style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.25rem' }}>Abteilung</Text>
          <Select onChange={(e: any) => setDepartmentFilter(e.detail.selectedOption.value)} style={{ width: '100%' }}>
            <Option value="">Alle Abteilungen</Option>
            {departments.map(dept => (<Option key={dept} value={dept}>{dept}</Option>))}
          </Select>
        </div>

        {(searchTerm || statusFilter || departmentFilter) && (
          <Button
            icon="decline"
            design="Transparent"
            onClick={() => { setSearchTerm(''); setStatusFilter(''); setDepartmentFilter(''); }}
            tooltip="Filter zurücksetzen"
          />
        )}
      </FlexBox>

      {/* Table Card */}
      <Card style={{ overflow: 'hidden' }}
        header={
          <CardHeader
            titleText={`${filteredActivities.length} von ${activities.length} Verarbeitungen`}
            subtitleText="Klicken Sie auf eine Zeile für Details"
            avatar={<Icon name="list" />}
          />
        }
      >
        {loading ? (
          <FlexBox alignItems="Center" justifyContent="Center" style={{ padding: '3rem' }}>
            <BusyIndicator active size="Large" />
          </FlexBox>
        ) : filteredActivities.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <Icon name="documents" style={{ fontSize: '3rem', color: 'var(--sapNeutralColor)', display: 'block', marginBottom: '1rem' }} />
            <Title level="H5" style={{ marginBottom: '0.5rem' }}>
              {activities.length === 0 ? 'Noch keine Verarbeitungen' : 'Keine Treffer'}
            </Title>
            <Text style={{ display: 'block', color: 'var(--sapNeutralTextColor)', marginBottom: '1rem' }}>
              {activities.length === 0
                ? 'Erstellen Sie Ihre erste Verarbeitungstätigkeit.'
                : 'Passen Sie Ihre Filterkriterien an.'}
            </Text>
            {activities.length === 0 && canCreate && (
              <Button icon="add" design="Emphasized" onClick={() => navigate('/processing/new')}>
                Neue Verarbeitung erstellen
              </Button>
            )}
          </div>
        ) : (
          <div style={{ padding: '0 1rem 1rem' }}>
          <Table
            style={{ width: '100%' }}
            onRowClick={(e: any) => {
              const index = parseInt(e.detail.row.dataset.index);
              navigate(`/processing/${filteredActivities[index].id}`);
            }}
            columns={
              <>
                <TableColumn><Text style={{ fontWeight: '600' }}>Name</Text></TableColumn>
                <TableColumn demandPopin minWidth={400}><Text style={{ fontWeight: '600' }}>Abteilung</Text></TableColumn>
                <TableColumn demandPopin minWidth={500}><Text style={{ fontWeight: '600' }}>Rechtsgrundlage</Text></TableColumn>
                <TableColumn><Text style={{ fontWeight: '600' }}>Status</Text></TableColumn>
                <TableColumn><Text style={{ fontWeight: '600' }}>Risiko</Text></TableColumn>
                <TableColumn demandPopin minWidth={600}><Text style={{ fontWeight: '600' }}>Aktualisiert</Text></TableColumn>
              </>
            }
          >
            {filteredActivities.map((activity, index) => (
              <TableRow key={activity.id} data-index={index} type="Active">
                <TableCell>
                  <div>
                    <Text style={{ fontWeight: '600' }}>{activity.name}</Text>
                    {activity.description && (
                      <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)', display: 'block', marginTop: '0.125rem' }}>
                        {activity.description.length > 70 ? activity.description.substring(0, 70) + '...' : activity.description}
                      </Text>
                    )}
                  </div>
                </TableCell>
                <TableCell><Text>{activity.department}</Text></TableCell>
                <TableCell><Text>{legalBasisLabels[activity.legal_basis]}</Text></TableCell>
                <TableCell>
                  <ObjectStatus state={statusConfig[activity.status].state as any} showDefaultIcon>
                    {statusConfig[activity.status].label}
                  </ObjectStatus>
                </TableCell>
                <TableCell>
                  {activity.risk_level ? (
                    <ObjectStatus state={riskConfig[activity.risk_level].state as any} showDefaultIcon>
                      {riskConfig[activity.risk_level].label}
                    </ObjectStatus>
                  ) : (
                    <Text style={{ color: 'var(--sapNeutralTextColor)' }}>-</Text>
                  )}
                </TableCell>
                <TableCell>
                  <Text>{new Date(activity.updated_at).toLocaleDateString('de-DE')}</Text>
                </TableCell>
              </TableRow>
            ))}
          </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
